<template>
  <div class="ticket-create-new-ticket__container">
    <organism-ticket-detail
      v-if="!isLoading"
      :status="status"
      :customer="customerData"
      :ticket="ticketData"
      :ticketId="ticketId"
      :ticketDetail="ticketDetailData"
      :ticketTimestamp="ticketTimestamp"
      :activityCommunityList="activityCommunityList"
      :pageForceClearFileValue="pageForceClearFileValue"
      @onClickDownloadFile="onClickDownloadFile"
      @onClickAddTicketActivity="onClickAddTicketActivity"
      @onRequestUploadAttachment="onRequestUploadAttachment"
      @onRequestDeleteAttachment="onRequestDeleteAttachment"
      @onRequestDeleteAttachmentFromThread="onRequestDeleteAttachmentFromThread"
    ></organism-ticket-detail>
  </div>
</template>

<script>
import { DateTime } from "luxon"
import {
  getExternalTopicList,
  getExternalTicketById,
  getExternalThreadByTicketId,
  addExternalThreadByTicketId,
  uploadExternalThreadAttachments,
  uploadExternalTicketAttachments,
  deleteExternalTicketAttachment,
  deleteExternalThreadAttachment,
  getExternalContractByIdcId,
} from "@/api/api-module1"
import mixins from "@/mixins"
import OrganismTicketDetail from "@/components/organisms/portal/external/ticket/OrganismTicketDetail.vue"

export default {
  name: "TicketDetail",
  components: { OrganismTicketDetail },
  mixins: [mixins],
  data() {
    return {
      // id
      id: "",

      // status
      status: "",

      // customer
      customerData: null,

      // ticket
      ticketData: null,

      // ticketDetail
      ticketDetailData: null,

      // ticket detail
      ticketTimestamp: null,
      activityCommunityList: [],

      priorityList: [
        { id: "CRITICAL", name: "Critical" },
        { id: "HIGH", name: "High" },
        { id: "MEDIUM", name: "Medium" },
        { id: "LOW", name: "Low" },
      ],
      sourceList: [
        { id: "TICKET", name: "Ticket" },
        { id: "PHONE", name: "Phone" },
        { id: "EMAIL", name: "Email" },
        { id: "OTHER", name: "Other" },
      ],

      topicList: [],

      pageForceClearFileValue: 0,
    }
  },
  computed: {
    isLoading() {
      return (
        !this.topicList ||
        !this.customerData ||
        (!this.ticketData && !this.ticketDetailData)
      )
    },
    ticketId() {
      return this.$route?.params?.ticketId.toString()
    },
  },
  watch: {
    ticketId: {
      deep: true,
      immediate: true,
      handler() {
        this.id = this.ticketId
        this.getTopicListFromAPI()
        this.getTicketDataFromAPI()
      },
    },
  },
  methods: {
    // get data
    async getTopicListFromAPI() {
      this.showLoading(true)
      // ticket
      await getExternalTopicList().then((response) => {
        this.topicList = response.map((topic) => {
          return {
            id: topic.topicId,
            name: topic.topicName,
          }
        })
      })
    },

    async getTicketDataFromAPI() {
      this.showLoading(true)
      await getExternalTicketById(this.id)
        .then((ticket) => {
          // status
          this.status = ticket.status

          // customer
          this.customerData = {
            name: ticket.contractCustomerName,
            email: ticket.contractEmails.split(",").join(", "),
            phone: ticket.contractPhones,
            idcId: ticket.contractId.toString().padStart(8, "0"),
            ticketId: ticket.ticketId,
            site: ticket.siteName,
          }

          // ticket
          this.ticketData = {
            topic: this.topicList.find((t) => t.id === ticket.topicId),
            subject: ticket.ticketSubject,
            detail: ticket.ticketDetail,
            attachments: ticket.attachments,
            priority: this.priorityList.find((p) => p.id === ticket.priority),
          }

          // ticketDetail
          this.ticketDetailData = {
            department: ticket?.responsibleDepartmentName,
            source: this.sourceList.find((s) => s.id === ticket.source),
            createdBy: ticket.openedBy,
            updatedBy: ticket.updatedBy,
          }

          // ticket detail
          this.ticketTimestamp = {
            createdAt: DateTime.fromISO(ticket.openedAt).toFormat(
              "dd/MM/yyyy HH:mm:ss"
            ),
            closedAt: ticket.closedAt
              ? DateTime.fromISO(ticket.closedAt).toFormat(
                  "dd/MM/yyyy HH:mm:ss"
                )
              : null,
            duration: this.getDuration(ticket.closedAt, ticket.openedAt),
          }
        })
        .then(() => this.getCustomerData(this.customerData.idcId))
        .catch(() => {
          this.toastError("Ticket Not Found")
          this.$router.push({ name: "externalTicketManagement" })
        })
        .finally(() => this.showLoading(false))

      await this.getActivitiesFromApi()
    },

    async getCustomerData(idcId) {
      this.showLoading(true)
      await getExternalContractByIdcId(idcId)
        .then((contract) => {
          const contractLocation = contract.packages.map(
            (item) => item.locationName
          )
          this.customerData.locations = contractLocation.join("\n")
        })
        .finally(() => this.showLoading(false))
    },

    async getActivitiesFromApi() {
      this.showLoading(true)
      this.activityCommunityList = []
      await getExternalThreadByTicketId(this.id)
        .then((threads) => {
          threads.forEach((thread) => {
            this.activityCommunityList.push({
              threadId: thread.threadId,
              name: thread.createdBy,
              detail: thread.threadMessage,
              datetime: DateTime.fromISO(thread.createdAt).toFormat(
                "dd/MM/yyyy HH:mm:ss"
              ),
              attachments: thread.attachments.map((attachment) => {
                return {
                  id: attachment.attachmentId,
                  name: attachment.fileName,
                  originalFileName: attachment.originalFileName,
                  path: attachment.attachmentPath,
                  createdBy: attachment.createdBy,
                }
              }),
            })
          })
        })
        .finally(() => this.showLoading(false))
    },

    // on click ticket
    onClickDownloadFile(attachment) {
      window.open(attachment.path, attachment.fileName)
    },

    async onClickAddTicketActivity(request, attachment) {
      this.showLoading(true)
      await addExternalThreadByTicketId(this.id, request)
        .then(async (response) => {
          if (response.status === "ok") {
            this.toastSuccess("Activity added successfully")
            const thread = response.data

            if (attachment) {
              await uploadExternalThreadAttachments(
                thread.threadId,
                this.id,
                attachment
              ).then((attachment) => {
                if (attachment.status === "ok")
                  this.toastSuccess("Attachment uploaded successfully")
                else this.toastError("Attachment uploaded failed")
              })
              .catch(() => {
                  this.toastError(
                    "Attachment uploaded failed. Please check the file size of your attachment and try again."
                  )
              })
            }
          } else this.toastError("Activity added failed")

          await this.getTicketDataFromAPI()
        })
        .finally(() => this.showLoading(false))
    },
    // get duration function
    getDuration(endDate, startDate) {
      if (!endDate) endDate = DateTime.now()

      // days
      const durationFromDays = parseInt(
        DateTime.fromISO(endDate)
          .diff(DateTime.fromISO(startDate), "days")
          .toObject().days
      )

      if (durationFromDays) return `${durationFromDays} days`

      // hours
      const durationFromHours = parseInt(
        DateTime.fromISO(endDate)
          .diff(DateTime.fromISO(startDate), "hours")
          .toObject().hours
      )

      if (durationFromHours) return `${durationFromHours} hours`

      // minutes
      const durationFromMinutes = parseInt(
        DateTime.fromISO(endDate)
          .diff(DateTime.fromISO(startDate), "minutes")
          .toObject().minutes
      )

      return `${durationFromMinutes} minutes`
    },

    onRequestDeleteAttachment(file) {
      let attachmentId = file.id
      this.sendRequestDeleteAttachmentToApi(this.id, attachmentId)
    },

    async sendRequestDeleteAttachmentToApi(ticketId, attachmentId) {
      this.showLoading(true)
      await deleteExternalTicketAttachment(ticketId, attachmentId)
        .then(async (response) => {
          if (response.status === "ok") {
            this.toastSuccess("Attachment deleted successfully")
          } else this.toastError("Attachment delete failed")
          await this.getTicketDataFromAPI()
        })
        .finally(() => this.showLoading(false))
    },

    onRequestUploadAttachment(ticketId, attachmentList) {
      this.sendRequestUploadAttachment(ticketId, attachmentList)
    },

    async sendRequestUploadAttachment(ticketId, attachmentList) {
      this.showLoading(true)
      await uploadExternalTicketAttachments(ticketId, attachmentList)
        .then(async (attachment) => {
          if (attachment.status === "ok") {
            this.toastSuccess("Attachment uploaded successfully")
            this.pageForceClearFileValue++
          } else this.toastError("Attachment uploaded failed")
          await this.getTicketDataFromAPI()
        })
        .catch(() => {
                  this.toastError(
                    "Attachment uploaded failed. Please check the file size of your attachment and try again."
                  )
              })
        .finally(() => this.showLoading(false))
    },

    onRequestDeleteAttachmentFromThread(file, thread) {
      let attachmentId = file.id
      let threadId = thread.threadId
      this.sendRequestDeleteThreadAttachmentToApi(
        this.id,
        threadId,
        attachmentId
      )
    },

    async sendRequestDeleteThreadAttachmentToApi(
      ticketId,
      threadId,
      attachmentId
    ) {
      this.showLoading(true)
      await deleteExternalThreadAttachment(ticketId, threadId, attachmentId)
        .then(async (response) => {
          if (response.status === "ok") {
            this.toastSuccess("Attachment deleted successfully")
          } else this.toastError("Attachment delete failed")
          await this.getTicketDataFromAPI()
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped>
.ticket-create-new-ticket__container {
  padding: 0 0 21px 0;
}
</style>
